import React, { useContext } from "react";
import "./style.scss";
import RegistrationContext from "../../contexts/RegistrationContext";
import {
  FileText,
  TriangleAlert,
  Bug,
  GraduationCap,
  ArrowBigUp,
  MapPin,
  NotebookPen,
  BadgeCheck,
} from "lucide-react";

import { ConfirmationOptions } from "../../types/confirmation";
import { VisaLetterApplication } from "./VisaLetterApplication";
import { Documents } from "./Documents";
import { StudentStatus } from "./StudentStatus";
import { EurAIStatus } from "./EurAIStatus";
import { Cancellation } from "./Cancellation";
import { RegisteredPapers } from "./RegisteredPapers";
import { WorkhshopSelection } from "./WorkhshopSelection";
import { TutorialSelection } from "./TutorialSelection";
import { RegistrationDetails } from "./RegistrationDetails";
import { Attendance } from "./Attendance";
import RegistrationCompleteIcon from "./Icons/RegistrationCompleteIcon";
import UpdateRegistration from "./UpdateRegistration";
import RightArrow from "./Icons/RightArrow";

export default function ConfirmationPage() {
  const {
    data,
    permissions,
    isImpersonating,
  } = useContext(RegistrationContext);

  const [activeOption, setActiveOption] = React.useState<ConfirmationOptions>(
    "Registration Details"
  );

  const payment_date = new Date(data.payment_date);
  const formatted_payment_date = payment_date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const updateRegistrationVisible = data.update_enabled === 1;
  const cancellationEnabled = true;
  return (
    <div className="overflow-y-scroll bg-slate-50">
      {/* Main Content */}
      <main className="container px-4 py-8">
        <div className="mb-8 flex flex-col items-center justify-center text-center">
          <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
            <RegistrationCompleteIcon />
          </div>
          <h1 className="text-2xl font-bold">Registration Complete</h1>
          <p className="text-gray-500">
            Thank you for your registration. Your payment has been processed
            successfully.
          </p>
        </div>

        <div className="mx-auto max-w-6xl">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-4">
            {/* Vertical Menu */}
            <div className="md:col-span-1">
              <div className="rounded-lg border bg-white shadow-sm">
                <nav className="flex flex-col">
                  {/* Registration Details */}
                  <button
                    onClick={() => setActiveOption("Registration Details")}
                    className="flex items-center justify-between border-b p-4 hover:bg-slate-50"
                  >
                    <div className="flex items-center gap-3">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="h-5 w-5 text-blue-600"
                      >
                        <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
                        <circle cx="12" cy="7" r="4" />
                      </svg>
                      <span className="font-medium">Registration Details</span>
                    </div>
                    <RightArrow className="h-4 w-4 text-gray-500" />
                  </button>
                  {/* Attendance and preferences */}
                  <button
                    onClick={() => setActiveOption("Attendance and Choices")}
                    className={`flex items-center justify-between border-b p-4
                      ${data.status === "pending"
                        ? "bg-red-200 hover:bg-red-300"
                        : "hover:bg-slate-50"
                      }
                      `}
                  >
                    <div className="flex items-center gap-3 ">
                      <MapPin
                        className={`${data.status === "pending"
                          ? "text-red-600"
                          : "text-blue-600"
                          } w-5`}
                      />
                      <span className="font-medium">Attendance & Choices</span>
                    </div>
                    <RightArrow className="h-4 w-4 text-gray-500" />
                  </button>
                  {/* Workshop Updates */}
                  {(
                    data.workshop_day1 +
                    data.workshop_day2 +
                    data.workshop_day3 > 0
                  ) && (
                      <button
                        onClick={() => setActiveOption("Workshop Updates")}
                        className={`flex items-center justify-between border-b p-4 
                      ${false
                            ? "bg-red-200 hover:bg-red-300"
                            : "hover:bg-slate-50"
                          }
                      `}
                      >
                        <div className="flex items-center gap-3 ">
                          <NotebookPen
                            className={`${false
                              ? "text-red-600"
                              : "text-blue-600"
                              } w-5`}
                          />
                          <span className="font-medium">Workshop selection</span>
                        </div>
                        <RightArrow className="h-4 w-4 text-gray-500" />
                      </button>
                    )}
                    {/* Tutorial Updates */}
                  {(
                 true
                  ) && (
                      <button
                        onClick={() => setActiveOption("Tutorial Updates")}
                        className={`flex items-center justify-between border-b p-4 
                      ${false
                            ? "bg-red-200 hover:bg-red-300"
                            : "hover:bg-slate-50"
                          }
                      `}
                      >
                        <div className="flex items-center gap-3 ">
                          <GraduationCap
                            className={`${false
                              ? "text-red-600"
                              : "text-blue-600"
                              } w-5`}
                          />
                          <span className="font-medium">Tutorial selection</span>
                        </div>
                        <RightArrow className="h-4 w-4 text-gray-500" />
                      </button>
                    )} 
                  {/* Visa Letter Application */}
                  {data.visa_letters.filter(
                    (x: any) =>
                      x.status === "empty" ||
                      x.status === "pending" ||
                      x.status === "ko"
                  ).length > 0 && (
                      <button
                        onClick={() => setActiveOption("Visa Letter Application")}
                        className="flex items-center justify-between border-b p-4 hover:bg-slate-50"
                      >
                        <div className="flex items-center gap-3">
                          <TriangleAlert className="text-orange-600 w-5" />
                          <span className="font-medium">
                            Visa Letter Application
                          </span>
                        </div>
                        <RightArrow className="h-4 w-4 text-gray-500" />
                      </button>
                    )}

                  {/* Documents */}
                  <button
                    onClick={() => setActiveOption("Documents")}
                    className="flex items-center justify-between border-b p-4 hover:bg-slate-50"
                  >
                    <div className="flex items-center gap-3">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="h-5 w-5 text-blue-600"
                      >
                        <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
                        <polyline points="14 2 14 8 20 8" />
                      </svg>
                      <span className="font-medium">Documents</span>
                    </div>
                    <RightArrow className="h-4 w-4 text-gray-500" />
                  </button>

                  {/* Papers */}
                  {data.papers.length > 0 && (
                    <button
                      onClick={() => setActiveOption("Registered Papers")}
                      className="flex items-center justify-between border-b p-4 hover:bg-slate-50"
                    >
                      <div className="flex items-center gap-3">
                        <FileText className="text-blue-600 w-5" />

                        <span className="font-medium">Registered Papers</span>
                      </div>
                      <RightArrow className="h-4 w-4 text-gray-500" />
                    </button>
                  )}

                  {/* Student Status */}
                  {["student", "student-paper"].includes(
                    data.registration_type
                  ) && (
                      <button
                        onClick={() => setActiveOption("Student Status")}
                        className={`flex items-center justify-between border-b p-4 
                      ${data.student_validation !== "ok"
                            ? "bg-red-200 hover:bg-red-300"
                            : "hover:bg-slate-50"
                          }
                      `}
                      >
                        <div className="flex items-center gap-3">
                          <GraduationCap
                            className={`${data.student_validation === "ko"
                              ? "text-red-600"
                              : "text-blue-600"
                              } w-5`}
                          />
                          <span className="font-medium">Student Status</span>
                        </div>
                        <RightArrow className="h-4 w-4 text-gray-500" />
                      </button>
                    )}
                  {/* EurAI Membership */}
                  {(data.claims_eurai_member ||
                    data.eurai_validation === "ko") && (
                      <button
                        onClick={() => setActiveOption("EurAI Membership")}
                        className={`flex items-center justify-between border-b p-4
                      ${data.eurai_validation === "ko"
                            ? "bg-red-200 hover:bg-red-300"
                            : "hover:bg-slate-50"
                          }
                      `}
                      >
                        <div className="flex items-center gap-3">
                          <BadgeCheck
                            className={`${data.eurai_validation === "ko"
                              ? "text-red-600"
                              : "text-blue-600"
                              } w-5`}
                          />
                          <span className="font-medium">EurAI Membership</span>
                        </div>
                        <RightArrow className="h-4 w-4 text-gray-500" />
                      </button>
                    )}
                  {/* Update Registration */}
                  {updateRegistrationVisible && (
                    <button
                      onClick={() => setActiveOption("Update Registration")}
                      className="flex items-center justify-between  border-b  p-4 hover:bg-slate-50"
                    >
                      <div className="flex items-center gap-3">
                        <ArrowBigUp className="text-blue-600 w-5" />
                        <span className="font-medium">Update Registration</span>
                      </div>
                      <RightArrow className="h-4 w-4 text-gray-500" />
                    </button>
                  )}
                  {/* Request Cancellation */}
                  {cancellationEnabled && (
                  <button
                    onClick={() => setActiveOption("Request Cancellation")}
                    className="flex items-center justify-between  border-b p-4 hover:bg-slate-50"
                  >
                    <div className="flex items-center gap-3">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="h-5 w-5 text-blue-600"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <line x1="15" x2="9" y1="9" y2="15" />
                        <line x1="9" x2="15" y1="9" y2="15" />
                      </svg>
                      <span className="font-medium">Request Cancellation</span>
                    </div>
                    <RightArrow className="h-4 w-4 text-gray-500" />
                  </button>
                  )}
                </nav>
              </div>

              {/* Debug Option */}
              {(permissions.includes('ijcai_impersonate') || isImpersonating) && (
                <div className="md:col-span-1 mt-4">
                  <div className="rounded-lg border bg-white shadow-sm">
                    <nav className="flex flex-col">
                      <button
                        onClick={() => setActiveOption("Debug")}
                        className="flex items-center justify-between border-b p-4 hover:bg-slate-50"
                      >
                        <div className="flex items-center gap-3">
                          <Bug className="text-blue-600 text-gray-500 w-5" />
                          <span className="font-medium">Debug</span>
                        </div>
                        <RightArrow className="h-4 w-4 text-gray-500" />
                      </button>
                    </nav>
                  </div>
                </div>
              )}

              <div className="mt-4 rounded-lg border bg-white p-4 shadow-sm">
                <div className="flex items-center gap-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-5 w-5 text-amber-500"
                  >
                    <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
                    <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
                  </svg>
                  <h3 className="font-medium">Registration Status</h3>
                </div>
                <div className="mt-2 text-sm">
                  <p className="flex items-center gap-1 font-medium text-green-600">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="h-4 w-4"
                    >
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                      <polyline points="22 4 12 14.01 9 11.01" />
                    </svg>
                    Payment Complete
                  </p>
                  <p className="mt-1 text-gray-500">
                    Registration Code: {data.registration_code}
                  </p>

                  <p className="text-gray-500">
                    Completed on {formatted_payment_date}
                  </p>
                </div>
              </div>
            </div>

            {/* Content Area */}
            <div className="md:col-span-3">
              {activeOption === "Registration Details" && (
                <RegistrationDetails />
              )}
              {activeOption === "Attendance and Choices" && <Attendance />}

              {activeOption === "Visa Letter Application" && (
                <VisaLetterApplication />
              )}
              {activeOption === "Documents" && <Documents />}
              {activeOption === "Student Status" && (
                <StudentStatus setActiveOption={() => setActiveOption} />
              )}
              {activeOption === "EurAI Membership" && <EurAIStatus />}
              {activeOption === "Workshop Updates" && <WorkhshopSelection />}
              {activeOption === "Tutorial Updates" && <TutorialSelection />}
              {activeOption === "Update Registration" && <UpdateRegistration />}
              {activeOption === "Request Cancellation" && <Cancellation />}

              {activeOption === "Registered Papers" && <RegisteredPapers />}
              {activeOption === "Debug" && <pre>{JSON.stringify(data, null, 2)}</pre>}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
